<template>
  <svg
    :viewBox="`0 0 ${width} ${height}`"
    width="100%"
    style="display: block"
    class="caption"
  >
    <g :transform="`translate(0,${height - periodBarHeight - 4})`">
      <g v-for="p in periods" :key="p.start">
        <rect
          :x="x(p.start - 0.5)"
          :width="x(p.end + 0.5) - x(p.start - 0.5)"
          :height="periodBarHeight"
          fill="none"
          stroke="#000"
        ></rect>
        <text
          :x="(x(p.end + 0.5) + x(p.start - 0.5)) / 2"
          :y="periodBarHeight / 2"
          dy="0.32em"
          text-anchor="middle"
        >
          {{ p.location }}
        </text>
      </g>
    </g>
    <g :transform="`translate(0,${height - margin.bottom})`">
      <g
        v-for="(t, i) in allYears"
        :key="t"
        :transform="`translate(${x(t)}, 0)`"
        :opacity="getYearOpacity(t)"
      >
        <text fill="currentColor" y="9" dy="0.71em" text-anchor="middle">
          {{ formatYearTick(t, i, allYears) }}
        </text>
        <line stroke="currentColor" y="6"></line>
      </g>
    </g>
    <g :transform="`translate(${margin.left},0)`">
      <g
        v-for="t in y.ticks(height / 40)"
        :key="t"
        :transform="`translate(0,${y(t)})`"
      >
        <text fill="currentColor" x="-9" dy="0.32em" text-anchor="end">
          {{ formatYTick(t) }}
        </text>
        <line
          stroke="currentColor"
          stroke-opacity="0.3"
          :x1="width - margin.left - margin.right"
        ></line>
      </g>
    </g>
    <g>
      <g
        v-for="d in dataByYear"
        :key="d.year"
        :transform="`translate(${x(d.year)},0)`"
      >
        <rect
          v-for="s in d.poemsBySentiment"
          :key="s.sentiment"
          :x="barX(s)"
          :y="barY(s)"
          :width="barWidth"
          :height="barHeight(s)"
          :fill="color(s.sentiment)"
          :fill-opacity="getOpacity({ year: d.year, sentiment: s.sentiment })"
        ></rect>
      </g>
    </g>
    <g :transform="`translate(0,${margin.top})`" class="brush" ref="brush"></g>
  </svg>
</template>

<script>
import { scaleLinear, scaleBand } from "d3-scale";
import { max } from "d3-array";
import { brushX } from "d3-brush";
import { select, event } from "d3-selection";

const getEvent = () => event;

export default {
  props: {
    width: {
      type: Number,
      required: true
    },
    periodBarHeight: {
      type: Number,
      required: true
    },
    yearRange: {
      type: Array,
      required: true
    },
    brushedYearRange: {
      type: Array,
      required: true
    },
    dataByYear: {
      type: Array,
      required: true
    },
    periods: {
      type: Array,
      required: true
    },
    allYears: {
      type: Array,
      required: true
    },
    color: {
      type: Function,
      required: true
    },
    getOpacity: {
      type: Function,
      required: true
    },
    getYearOpacity: {
      type: Function,
      required: true
    },
    formatYearTick: {
      type: Function,
      required: true
    },
    yValueMode: {
      type: String,
      required: true
    },
    formatYTick: {
      type: Function,
      required: true
    },
    sentiments: {
      type: Array,
      required: true
    },
    barArrangementMode: {
      type: String,
      required: true
    }
  },
  data: () => ({
    height: 112,
    margin: { top: 8, right: 20, bottom: 48, left: 40 },
    brushstartedYearRange: []
  }),
  computed: {
    x() {
      return scaleLinear()
        .domain([this.yearRange[0] - 0.5, this.yearRange[1] + 0.5])
        .range([this.margin.left, this.width - this.margin.right]);
    },
    xInner() {
      return scaleBand()
        .domain(this.sentiments)
        .range([0, this.rectWidth])
        .padding(0.1);
    },
    maxY() {
      let maxY;
      if (this.barArrangementMode === "stacked") {
        maxY = max(this.dataByYear.map(d => d.total[this.yValueMode]));
      } else if (this.barArrangementMode === "grouped") {
        maxY = max(this.dataByYear, d =>
          max(d.poemsBySentiment, v => v.value[this.yValueMode])
        );
      }
      return maxY;
    },
    y() {
      return scaleLinear()
        .domain([0, this.maxY])
        .range([this.height - this.margin.bottom, this.margin.top]);
    },
    rectWidth() {
      return [this.x(1) - this.x(0)] * 0.8;
    },
    brush() {
      return brushX()
        .extent([
          [this.margin.left, -0.5],
          [
            this.width - this.margin.right,
            this.height - this.margin.bottom - this.margin.top + 0.5
          ]
        ])
        .on("start", this.brushstarted)
        .on("brush", this.brushed)
        .on("end", this.brushended);
    },
    barWidth() {
      let width;
      if (this.barArrangementMode === "stacked") {
        width = this.rectWidth;
      } else if (this.barArrangementMode === "grouped") {
        width = this.xInner.bandwidth();
      }
      return width;
    }
  },
  watch: {
    x() {
      // x change is triggered by window width resize
      // We need to update the brush selection manually to keep it in sync with the data
      select(this.$refs.brush).call(this.brush);
      this.syncBrush();
    },
    brushedYearRange() {
      this.syncBrush();
    }
  },
  mounted() {
    // Set up brush with custom handles
    const gBrush = select(this.$refs.brush).call(this.brush);
    gBrush
      .selectAll(".handle--custom")
      .data([{ type: "w" }, { type: "e" }])
      .enter()
      .append("path")
      .attr("class", "handle--custom")
      .attr("stroke", "currentColor")
      .attr("cursor", "ew-resize")
      .attr("d", this.brushResizePath);
    gBrush.call(this.brush.move, this.x.range());
  },
  methods: {
    barX(s) {
      let x;
      if (this.barArrangementMode === "stacked") {
        x = -this.rectWidth / 2;
      } else if (this.barArrangementMode === "grouped") {
        x = -this.rectWidth / 2 + this.xInner(s.sentiment);
      }
      return x;
    },
    barY(s) {
      let y;
      if (this.barArrangementMode === "stacked") {
        y = this.y(s.stacked[this.yValueMode][1]);
      } else if (this.barArrangementMode === "grouped") {
        y = this.y(s.value[this.yValueMode]);
      }
      return y;
    },
    barHeight(s) {
      let height;
      if (this.barArrangementMode === "stacked") {
        height =
          this.y(s.stacked[this.yValueMode][0]) -
          this.y(s.stacked[this.yValueMode][1]);
      } else if (this.barArrangementMode === "grouped") {
        height = this.y(0) - this.y(s.value[this.yValueMode]);
      }
      return height;
    },
    brushSelectionToYearRange(s) {
      return s
        .map(this.x.invert, this.x)
        .map((d, i) => (i ? Math.floor(d) : Math.ceil(d)));
    },
    syncBrush() {
      select(this.$refs.brush).call(
        this.brush.move,
        this.brushedYearRange
          .map((d, i) => (i ? d + 0.5 : d - 0.5))
          .map(this.x, this.x)
      );
    },
    brushstarted() {
      this.brushstartedYearRange = this.brushedYearRange.slice();
      this.brushed();
    },
    brushed() {
      const s = getEvent().selection;
      if (s) {
        const ty = -(this.height - this.margin.bottom - this.margin.top) / 4;
        select(this.$refs.brush)
          .selectAll(".handle--custom")
          .attr("transform", (d, i) => "translate(" + [s[i], ty] + ")");
      }
    },
    brushended() {
      // Snap brush to year gaps
      const event = getEvent();
      const sourceEvent = event.sourceEvent;
      if (!sourceEvent || sourceEvent.type === "end") return; // Break infinite loop
      const s = event.selection || this.x.range();
      const brushedYearRange = this.brushSelectionToYearRange(s);
      if (
        JSON.stringify(brushedYearRange) !==
        JSON.stringify(this.brushstartedYearRange)
      ) {
        this.$emit("brush-ended", brushedYearRange);
      }
    },
    brushResizePath(d) {
      const e = +(d.type == "e"),
        x = e ? 1 : -1,
        y = (this.height - this.margin.top - this.margin.bottom) / 2;
      return (
        "M" +
        0.5 * x +
        "," +
        y +
        "A6,6 0 0 " +
        e +
        " " +
        6.5 * x +
        "," +
        (y + 6) +
        "V" +
        (2 * y - 6) +
        "A6,6 0 0 " +
        e +
        " " +
        0.5 * x +
        "," +
        2 * y +
        "Z" +
        "M" +
        2.5 * x +
        "," +
        (y + 8) +
        "V" +
        (2 * y - 8) +
        "M" +
        4.5 * x +
        "," +
        (y + 8) +
        "V" +
        (2 * y - 8)
      );
    }
  }
};
</script>
